'use client';
/**
 * LA CONVERSATION QUI A MENÉ À LA NC (RQ, 03/11/2026).
 *
 * « Je voudrais avoir accès à la discussion qui permet d'aboutir à une NC. »
 * Le récit du déclarant, les questions de l'assistant, ses réponses — tels que
 * tapés —, puis la fiche telle que l'assistant l'avait remplie AVANT la
 * relecture : l'écart avec la fiche actuelle se signale champ par champ.
 *
 * Ne s'affiche qu'à la RQ et au déclarant (le moteur refuse les autres), et
 * seulement pour une NC rédigée avec l'assistant après le 03/11/2026 : rien
 * n'était gardé avant. Une NC sans conversation n'affiche rien — l'absence
 * n'est pas une affirmation qu'il n'y en a jamais eu.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { MarqueIA, Pastille } from '@/components/ui';
import { Bulle, type Echange, type FicheEntretien } from '@/components/EntretienDeclarationNC';
import { NC_GRAVITES, IMPACTS_RESULTATS, libelleCanal, natureMeta, natureDe, fmtDate } from '@/lib/meta';
import { fmtDateHeure } from '@/lib/dates';
import type { GedNC } from '@/lib/types';

interface Entretien { echanges: Echange[]; ficheProposee: FicheEntretien | null; declarantNom: string; enregistreLe: string }

const libelle = (liste: { key: string; label: string }[], cle?: string | null) => (cle ? liste.find((x) => x.key === cle)?.label || cle : '');

export function ConversationNC({ nc }: { nc: GedNC }) {
  const { session } = useGed();
  const autorise = session.roles.includes('rq') || (!!nc.detectePar && nc.detectePar === session.key);
  const [entretien, setEntretien] = React.useState<Entretien | null>(null);

  React.useEffect(() => {
    if (!autorise) return;
    let vivant = true;
    void fetch(`/api/ged/ged/nc/${nc.documentId}/entretien`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((d: { entretien?: Entretien | null } | null) => { if (vivant) setEntretien(d?.entretien || null); });
    return () => { vivant = false; };
  }, [autorise, nc.documentId]);

  if (!autorise || !entretien || !entretien.echanges.length) return null;
  const f = entretien.ficheProposee;
  const champs: { label: string; propose: string; actuel: string }[] = f ? [
    { label: 'Intitulé', propose: f.titre, actuel: nc.titre },
    { label: 'Description', propose: f.description, actuel: nc.description },
    { label: 'Nature', propose: natureMeta(f.nature)?.label || f.nature, actuel: natureMeta(natureDe(nc))?.label || natureDe(nc) },
    { label: 'Détectée par', propose: libelleCanal(f.canal), actuel: libelleCanal(nc.canal) },
    { label: 'Gravité', propose: libelle(NC_GRAVITES, f.gravite), actuel: libelle(NC_GRAVITES, nc.gravite) },
    { label: 'Résultats patients touchés', propose: libelle(IMPACTS_RESULTATS, f.impactResultats), actuel: libelle(IMPACTS_RESULTATS, nc.impactResultats) },
    { label: 'Correction immédiate', propose: f.correction, actuel: nc.correction || '' },
    { label: 'Date de détection', propose: f.dateDetection ? fmtDate(f.dateDetection) : '', actuel: nc.dateDetection ? fmtDate(nc.dateDetection) : '' },
  ].filter((c) => c.propose) : [];

  return (
    <details style={{ border: '1px solid var(--ia-bord)', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)' }}>
      <summary style={{ cursor: 'pointer', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <MarqueIA />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ia-encre)' }}>Conversation avec l’assistant</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {entretien.declarantNom}{entretien.enregistreLe ? ` · ${fmtDateHeure(entretien.enregistreLe)}` : ''}
        </span>
      </summary>
      <div style={{ display: 'grid', gap: 10, padding: '4px 14px 14px' }}>
        {entretien.echanges.map((e, i) => (
          <Bulle key={i} de={e.role}>
            {e.role === 'assistant' && e.texte.includes('\n')
              ? <ol style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 4 }}>
                  {e.texte.split('\n').map((q, j) => <li key={j}>{q}</li>)}
                </ol>
              : e.texte}
          </Bulle>
        ))}
        {f && (
          <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>
              La fiche remplie par l’assistant, avant relecture
            </div>
            {champs.map((c) => (
              <div key={c.label} style={{ display: 'grid', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{c.label}</span>
                  {c.propose.trim() !== c.actuel.trim() && (
                    <Pastille ton="warning" compacte title={c.actuel ? `Fiche actuelle : ${c.actuel}` : 'Vide sur la fiche actuelle'}>
                      Différent de la fiche actuelle
                    </Pastille>
                  )}
                </div>
                <span style={{ fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{c.propose}</span>
              </div>
            ))}
            {f.manques && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                <strong>Ce qui manquait selon l’assistant :</strong> {f.manques}
              </div>
            )}
          </div>
        )}
      </div>
    </details>
  );
}
